import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getSiteSettings } from '../lib/api';
import './VerticalGardenPage.css';
import { useSEO } from '../lib/seo';

// ── Static fallback data ──────────────────────────────────────────────────────
const DEFAULT_STATS = [
  { value: '200+', label: 'Installations across India' },
  { value: '40+',  label: 'Plant varieties available' },
  { value: '15 yr',label: 'Avg lifespan with care' },
  { value: '4.9/5',label: 'Client satisfaction' },
];

const DEFAULT_TYPES = [
  {
    title: 'Indoor Living Walls',
    desc: 'Transform any interior wall into a breathing green canvas. Ideal for offices, lobbies, homes, and retail spaces. Low-light varieties selected for long-term health.',
    img: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&h=440&fit=crop&auto=format&q=80',
    tag: 'Most Popular',
    plants: ['Pothos', 'Peace Lily', 'Ferns', 'Philodendron'],
  },
  {
    title: 'Outdoor Green Facades',
    desc: 'Facade systems that climb and cover exterior walls, balconies, and compound walls. Built for Indian summers and monsoons.',
    img: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=440&fit=crop&auto=format&q=80',
    tag: null,
    plants: ['Jasmine', 'Bougainvillea', 'Money Plant', 'Creepers'],
  },
  {
    title: 'Freestanding Planter Walls',
    desc: 'Modular panels that stand independently — no wall drilling required. Perfect for rentals, events, and temporary installations.',
    img: 'https://images.unsplash.com/photo-1598928506311-c55ded91a20c?w=600&h=440&fit=crop&auto=format&q=80',
    tag: 'No Drilling',
    plants: ['Succulents', 'Air Plants', 'Moss', 'Herbs'],
  },
  {
    title: 'Terrace & Rooftop Gardens',
    desc: 'Vertical trellis systems paired with containers to maximise green cover on terraces, rooftops, and small balconies.',
    img: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=600&h=440&fit=crop&auto=format&q=80',
    tag: null,
    plants: ['Vegetables', 'Herbs', 'Climbing Roses', 'Creepers'],
  },
  {
    title: 'Restaurant & Café Installs',
    desc: 'Lush statement walls that photograph well and improve guest experience. Includes ongoing maintenance visits.',
    img: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=440&fit=crop&auto=format&q=80',
    tag: 'Commercial',
    plants: ['Tropical Mix', 'Moss Art', 'Ferns', 'Pothos'],
  },
  {
    title: 'Herb & Edible Walls',
    desc: "Grow your own kitchen garden vertically. Fresh herbs and greens within arm's reach, installed in kitchens, balconies, and café prep areas.",
    img: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&h=440&fit=crop&auto=format&q=80',
    tag: 'Edible',
    plants: ['Basil', 'Mint', 'Coriander', 'Curry Leaf'],
  },
];

const DEFAULT_STEPS = [
  { num: '1', title: 'Share your space',        desc: 'Send us photos, dimensions, and your budget. Our team reviews it within 24 hours.' },
  { num: '2', title: 'Get a design proposal',   desc: 'We suggest plant varieties, frame options, and irrigation — with a visual mockup.' },
  { num: '3', title: 'Installation day',         desc: 'Our trained team arrives with everything needed. Most installs finish in a single day.' },
  { num: '4', title: 'We keep it growing',       desc: "Monthly care visits, plant replacements, and a helpline that picks up — we stay involved." },
];

const DEFAULT_BENEFITS = [
  {
    title: 'Proven to reduce stress',
    text: "Studies show plants in the visual field reduce cortisol levels. A green wall visible from your desk is never decorative — it's functional.",
  },
  {
    title: 'Better air quality',
    text: 'Dense plantings filter VOCs, absorb CO₂, and raise indoor humidity — measurable differences in enclosed spaces.',
  },
  {
    title: 'No floor space lost',
    text: 'Vertical systems use zero floor space. An 8 sq ft wall panel carries 40–60 plants that would otherwise need a dozen individual pots.',
  },
  {
    title: 'Thermal insulation',
    text: 'A dense living wall reduces heat transfer through exterior walls by up to 30% — cooler interiors, lower electricity bills.',
  },
];

const DEFAULT_BENEFIT_ICONS = [
  <svg key="heart" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  <svg key="air"   width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 0 0 1.46 1.32C7.5 19 12 17 17 8z"/><path d="M3.82 19.34C4.5 16 6 13 9 11"/></svg>,
  <svg key="space" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h6"/></svg>,
  <svg key="sun"   width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
];

const DEFAULT_GALLERY = [
  { url: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=900&h=1100&fit=crop&auto=format&q=80', label: 'Office lobby installation, Bengaluru', tall: true },
  { url: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&h=400&fit=crop&auto=format&q=80', label: 'Herb wall, home kitchen' },
  { url: 'https://images.unsplash.com/photo-1598928506311-c55ded91a20c?w=600&h=400&fit=crop&auto=format&q=80', label: 'Freestanding panel, retail store' },
  { url: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&h=400&fit=crop&auto=format&q=80', label: 'Living wall, café, Mumbai' },
  { url: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=600&h=400&fit=crop&auto=format&q=80', label: 'Terrace trellis, residential' },
];

const DEFAULT_FAQS = [
  { q: 'How much maintenance does a vertical garden need?',  a: 'Indoor living walls need watering 2–3 times a week and occasional leaf-wipe. Outdoor ones can be on a drip irrigation system that runs automatically. Our maintenance plan includes monthly visits from our care team.' },
  { q: 'Can I install one in a rental flat?',                a: 'Yes — we have freestanding panel options that require no wall drilling and can move with you. For wall-mounted systems, we use fixings that leave minimal marks and can be patched on removal.' },
  { q: "What's the minimum size you install?",               a: 'Our smallest panels start at 2 sq ft. A single panel makes a meaningful statement; most homes opt for 6–15 sq ft across one focal wall.' },
  { q: 'Do the plants survive Indian summers?',              a: "We select plant varieties based on your local climate and the wall's sun exposure. Indoor systems use low-light tropicals built for AC environments. Outdoor systems use heat-tolerant, native, and adapted species." },
  { q: "What's included in the installation quote?",         a: 'Frame, substrate, plants, irrigation hardware, installation labour, and a 30-day aftercare call. Pricing varies by system type, plant selection, and wall size. Most homes spend ₹4,000–₹12,000 per sq ft for a complete installed wall.' },
  { q: 'How long does installation take?',                   a: "A typical residential living wall (10–20 sq ft) is installed in 4–6 hours. Larger commercial installs may span two days. We handle all the mess — you'll get a finished wall, not a project." },
];

const DEFAULT_INTRO_CHECKS = [
  'Farm-to-wall plants, grown by us',
  'End-to-end installation, no subcontractors',
  'Ongoing care plans available',
  'Custom designs for any brief',
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function sectionStyle(sec) {
  const style = {};
  if (sec?.bgColor) style.backgroundColor = sec.bgColor;
  if (sec?.paddingTop    != null) style.paddingTop    = sec.paddingTop    + 'px';
  if (sec?.paddingBottom != null) style.paddingBottom = sec.paddingBottom + 'px';
  if (sec?.paddingSide   != null) { style.paddingLeft = sec.paddingSide + 'px'; style.paddingRight = sec.paddingSide + 'px'; }
  return style;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function VerticalGardenPage() {
  useSEO({
    title: 'Vertical Garden — Wall Plants & Green Walls',
    description: 'Create a stunning vertical garden for your home or office with Naturiq. Expert design and installation of living green walls across India.',
    url: '/vertical-garden',
  });

  const [openFaq, setOpenFaq] = useState(null);
  const [sections, setSections] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);
  const [loading, setLoading] = useState(true);

  // Apply a vgSections array (from API or postMessage) into state
  const applyVgSections = useCallback((rawSections) => {
    if (!Array.isArray(rawSections) || rawSections.length === 0) return;
    const map = {};
    rawSections.forEach(s => { map[s.key] = s; });
    setSections(map);
    setSectionOrder(
      [...rawSections]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter(s => s.enabled !== false)
        .map(s => s.key)
    );
  }, []);

  // Initial fetch from API
  useEffect(() => {
    getSiteSettings()
      .then(data => applyVgSections(data.vgSections))
      .catch(() => {/* use defaults silently */})
      .finally(() => setLoading(false));
  }, [applyVgSections]);

  // Admin live-preview bridge — when embedded in the VG editor iframe,
  // listens for postMessage'd vgSections so unsaved edits appear instantly.
  const [highlightedSection, setHighlightedSection] = useState(null);

  useEffect(() => {
    const isEmbedded = window.self !== window.top;
    if (!isEmbedded) return;

    const handleMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.source !== 'naturiq-admin-preview') return;
      if (msg.type === 'vgSections' && Array.isArray(msg.payload) && msg.payload.length > 0) {
        applyVgSections(msg.payload);
      }
      if (msg.type === 'highlightSection') {
        setHighlightedSection(msg.payload || null);
      }
    };
    window.addEventListener('message', handleMessage);

    // Tell the admin editor this frame is ready to receive updates
    window.parent.postMessage({ source: 'naturiq-preview-frame', type: 'ready' }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, [applyVgSections]);

  const sec = (key) => sections[key] || {};
  const isHidden = (key) => sectionOrder.length > 0 && !sectionOrder.includes(key);

  const toggleFaq = (i) => setOpenFaq(prev => (prev === i ? null : i));

  // ── Hero data ───────────────────────────────────────────────────────────────
  const hero = sec('hero');
  const heroImages = Array.isArray(hero.images) && hero.images.length > 0 ? hero.images : null;
  const heroImgSrc = heroImages ? heroImages[0].url : 'https://www.gardenstead.com/wp-content/uploads/2021/11/vertical-wall-gardening.jpg';
  const heroEyebrow  = hero.eyebrow   || 'Vertical Garden Solutions';
  const heroTitle    = hero.title     || 'Turn Any Wall Into a Living Garden.';
  const heroSubtitle = hero.subtitle  || "Designed, installed, and maintained by Naturiq's green team. Indoor and outdoor systems for homes, offices, restaurants, and commercial spaces across India.";
  const heroCtaText  = hero.ctaText   || 'Get a Free Quote';
  const heroCtaLink  = hero.ctaLink   || '#enquire';
  const heroCtaText2 = hero.ctaText2  || 'Browse Plants';
  const heroCtaLink2 = hero.ctaLink2  || '/collections';
  const heroStats    = Array.isArray(hero.stats) && hero.stats.length > 0 ? hero.stats : DEFAULT_STATS;

  // ── Intro data ──────────────────────────────────────────────────────────────
  const intro = sec('intro');
  const introEyebrow   = intro.eyebrow     || 'Why a vertical garden';
  const introTitle     = intro.title       || 'More green, same footprint.';
  const introBody      = intro.description || "Vertical gardens solve the single biggest obstacle to bringing nature indoors: space. A single 10 sq ft living wall holds as many plants as 40 individual pots — with zero counter space consumed.\n\nNaturiq designs, installs, and maintains living walls across India. We grow the plants ourselves, so every wall ships from a Naturiq farm — never a middleman.";
  const introChecks    = Array.isArray(intro.checks) && intro.checks.length > 0 ? intro.checks.map(c => c.text) : DEFAULT_INTRO_CHECKS;
  const introCtaText   = intro.ctaText     || 'Start your project';
  const introCtaLink   = intro.ctaLink     || '#enquire';
  const introImage     = intro.introImage  || 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=700&h=920&fit=crop&auto=format&q=80';
  const introBadgeVal  = intro.badgeValue  || '40+';
  const introBadgeLbl  = intro.badgeLabel  || 'plants per 10 sq ft panel';

  // ── Types data ──────────────────────────────────────────────────────────────
  const types = sec('types');
  const typesEyebrow  = types.eyebrow  || 'What we install';
  const typesTitle    = types.title    || 'Six ways to go vertical';
  const typesSub      = types.subtitle || 'From a small kitchen herb wall to a 200 sq ft office statement — we design systems around the space, not the other way around.';
  const typeCards     = Array.isArray(types.typeCards) && types.typeCards.length > 0 ? types.typeCards : DEFAULT_TYPES;

  // ── How it works data ───────────────────────────────────────────────────────
  const how = sec('howItWorks');
  const howEyebrow  = how.eyebrow  || 'The process';
  const howTitle    = how.title    || 'From enquiry to installation in four steps';
  const howSub      = how.subtitle || "We handle everything — design, procurement, installation, and aftercare. Your only job is deciding where the wall goes.";
  const howSteps    = Array.isArray(how.steps) && how.steps.length > 0 ? how.steps : DEFAULT_STEPS;

  // ── Benefits data ───────────────────────────────────────────────────────────
  const ben = sec('benefits');
  const benEyebrow  = ben.eyebrow  || 'Why it matters';
  const benTitle    = ben.title    || 'Not just beautiful. Functional.';
  const benSub      = ben.subtitle || 'Every vertical garden does real, measurable work inside a space.';
  const benefits    = Array.isArray(ben.benefits) && ben.benefits.length > 0 ? ben.benefits : DEFAULT_BENEFITS;

  // ── Gallery data ────────────────────────────────────────────────────────────
  const gal = sec('gallery');
  const galEyebrow = gal.eyebrow || 'Our work';
  const galTitle   = gal.title   || "Walls we've brought to life";
  const gallery    = Array.isArray(gal.galleryImages) && gal.galleryImages.length > 0 ? gal.galleryImages : DEFAULT_GALLERY;

  // ── FAQ data ────────────────────────────────────────────────────────────────
  const faqSec  = sec('faq');
  const faqEyebrow = faqSec.eyebrow || 'Questions';
  const faqTitle   = faqSec.title   || 'Things people ask before they book';
  const faqs       = Array.isArray(faqSec.faqItems) && faqSec.faqItems.length > 0 ? faqSec.faqItems : DEFAULT_FAQS;

  // ── CTA data ────────────────────────────────────────────────────────────────
  const cta = sec('cta');
  const ctaTitle    = cta.title    || 'Ready to grow your walls?';
  const ctaSub      = cta.subtitle || "Send us your space details and we'll turn it around with a design proposal in 24 hours.";
  const ctaBtnText  = cta.ctaText  || 'Get a Free Quote';
  const ctaBtnLink  = cta.ctaLink  || '/contact';
  const ctaBtn2Text = cta.ctaText2 || 'Chat on WhatsApp';
  const ctaBtn2Link = cta.ctaLink2 || 'https://wa.me/917696999018';

  // Helper: render a CTA link as either a <Link> or <a>
  const CtaLink = ({ href, className, children }) =>
    href && (href.startsWith('http') || href.startsWith('https') || href.startsWith('//'))
      ? <a href={href} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
      : <Link to={href || '#'} className={className}>{children}</Link>;

  // Render sections in saved order when we have data, otherwise natural order
  const orderedKeys = sectionOrder.length > 0 ? sectionOrder : ['hero','intro','types','howItWorks','benefits','gallery','faq','cta'];

  const sectionComponents = {
    hero: (
      !isHidden('hero') && (
        <section key="hero" className="vg-hero" style={sectionStyle(hero)}>
          <img src={heroImgSrc} alt="Lush green vertical garden wall with tropical plants" className="vg-hero__img" />
          <div className="vg-hero__overlay" />
          <div className="vg-hero__content">
            <p className="vg-hero__eyebrow">
              <span className="vg-hero__eyebrow-bar" />
              {heroEyebrow}
            </p>
            <h1 className="vg-hero__title" dangerouslySetInnerHTML={{ __html: heroTitle.replace(/\n/g, '<br/>') }} />
            <p className="vg-hero__sub">{heroSubtitle}</p>
            <div className="vg-hero__actions">
              <CtaLink href={heroCtaLink} className="vg-btn vg-btn--light">{heroCtaText}</CtaLink>
              <CtaLink href={heroCtaLink2} className="vg-btn vg-btn--outline">{heroCtaText2}</CtaLink>
            </div>
          </div>
          <div className="vg-hero__stats">
            {heroStats.map((s, i) => (
              <div key={i} className="vg-hero__stat">
                <span className="vg-hero__stat-val">{s.value}</span>
                <span className="vg-hero__stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
        </section>
      )
    ),

    intro: (
      !isHidden('intro') && (
        <section key="intro" className="vg-intro" style={sectionStyle(intro)}>
          <div className="vg-intro__text">
            <p className="vg-intro__eyebrow">{introEyebrow}</p>
            <h2 className="vg-intro__title">{introTitle}</h2>
            {introBody.split('\n\n').map((para, i) => (
              <p key={i} className="vg-intro__body">{para}</p>
            ))}
            <div className="vg-intro__checks">
              {introChecks.map((c, i) => (
                <span className="vg-intro__check" key={i}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {c}
                </span>
              ))}
            </div>
            <CtaLink href={introCtaLink} className="vg-btn vg-btn--forest">{introCtaText}</CtaLink>
          </div>
          <div className="vg-intro__visual">
            <div className="vg-intro__img-wrap">
              <img src={introImage} alt="Close up of a lush green vertical garden panel" className="vg-intro__img" />
              {(introBadgeVal || introBadgeLbl) && (
                <div className="vg-intro__badge">
                  <div className="vg-intro__badge-val">{introBadgeVal}</div>
                  <div className="vg-intro__badge-lbl">{introBadgeLbl}</div>
                </div>
              )}
            </div>
          </div>
        </section>
      )
    ),

    types: (
      !isHidden('types') && (
        <section key="types" className="vg-types" id="types" style={sectionStyle(types)}>
          <div className="vg-types__inner">
            <div className="vg-section-head">
              <p className="vg-section-head__eyebrow">{typesEyebrow}</p>
              <h2 className="vg-section-head__title">{typesTitle}</h2>
              <p className="vg-section-head__sub">{typesSub}</p>
            </div>
            <div className="vg-types__grid">
              {typeCards.map((t, i) => (
                <div key={i} className="vg-type-card">
                  <div className="vg-type-card__img-wrap">
                    {t.img && <img src={t.img} alt={t.title} className="vg-type-card__img" />}
                    {t.tag && <span className="vg-type-card__tag">{t.tag}</span>}
                  </div>
                  <div className="vg-type-card__body">
                    <h3 className="vg-type-card__title">{t.title}</h3>
                    <p className="vg-type-card__desc">{t.desc}</p>
                    {Array.isArray(t.plants) && t.plants.length > 0 && (
                      <div className="vg-type-card__plants">
                        {t.plants.map(p => <span key={p} className="vg-type-card__plant">{p}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    ),

    howItWorks: (
      !isHidden('howItWorks') && (
        <section key="howItWorks" className="vg-how" style={sectionStyle(how)}>
          <div className="vg-section-head">
            <p className="vg-section-head__eyebrow">{howEyebrow}</p>
            <h2 className="vg-section-head__title">{howTitle}</h2>
            <p className="vg-section-head__sub">{howSub}</p>
          </div>
          <div className="vg-how__steps">
            {howSteps.map((s, i) => (
              <div key={i} className="vg-how__step">
                <div className="vg-how__step-num">{s.num || i + 1}</div>
                <h3 className="vg-how__step-title">{s.title}</h3>
                <p className="vg-how__step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )
    ),

    benefits: (
      !isHidden('benefits') && (
        <section key="benefits" className="vg-benefits" style={sectionStyle(ben)}>
          <div className="vg-benefits__inner">
            <div className="vg-section-head vg-section-head--light">
              <p className="vg-section-head__eyebrow">{benEyebrow}</p>
              <h2 className="vg-section-head__title">{benTitle}</h2>
              <p className="vg-section-head__sub">{benSub}</p>
            </div>
            <div className="vg-benefits__grid">
              {benefits.map((b, i) => (
                <div key={i} className="vg-benefit">
                  <div className="vg-benefit__icon">
                    {DEFAULT_BENEFIT_ICONS[i % DEFAULT_BENEFIT_ICONS.length]}
                  </div>
                  <h3 className="vg-benefit__title">{b.title}</h3>
                  <p className="vg-benefit__text">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    ),

    gallery: (
      !isHidden('gallery') && (
        <section key="gallery" className="vg-gallery" style={sectionStyle(gal)}>
          <div className="vg-gallery__inner">
            <div className="vg-section-head">
              <p className="vg-section-head__eyebrow">{galEyebrow}</p>
              <h2 className="vg-section-head__title">{galTitle}</h2>
            </div>
            <div className="vg-gallery__grid">
              {gallery.map((g, i) => (
                <div key={i} className={`vg-gallery__item${g.tall ? ' vg-gallery__item--tall' : ''}`}>
                  <img src={g.url || g.src} alt={g.label} />
                  {g.label && <div className="vg-gallery__item-label">{g.label}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    ),

    faq: (
      !isHidden('faq') && (
        <section key="faq" className="vg-faq" style={sectionStyle(faqSec)}>
          <div className="vg-section-head">
            <p className="vg-section-head__eyebrow">{faqEyebrow}</p>
            <h2 className="vg-section-head__title">{faqTitle}</h2>
          </div>
          <div className="vg-faq__list">
            {faqs.map((f, i) => (
              <div key={i} className="vg-faq__item">
                <button
                  className="vg-faq__question"
                  aria-expanded={openFaq === i}
                  onClick={() => toggleFaq(i)}
                >
                  {f.q}
                  <span className="vg-faq__icon">
                    {openFaq === i
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    }
                  </span>
                </button>
                <div className={`vg-faq__answer${openFaq === i ? ' vg-faq__answer--open' : ''}`}>
                  <p>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )
    ),

    cta: (
      !isHidden('cta') && (
        <div key="cta" className="vg-cta" id="enquire" style={sectionStyle(cta)}>
          <div>
            <h2 className="vg-cta__title">{ctaTitle}</h2>
            <p className="vg-cta__sub">{ctaSub}</p>
          </div>
          <div className="vg-cta__actions">
            <CtaLink href={ctaBtnLink} className="vg-btn vg-btn--light">{ctaBtnText}</CtaLink>
            {ctaBtn2Text && (
              ctaBtn2Link && (ctaBtn2Link.startsWith('http') || ctaBtn2Link.startsWith('https'))
                ? <a href={ctaBtn2Link} target="_blank" rel="noopener noreferrer" className="vg-btn vg-btn--outline">{ctaBtn2Text}</a>
                : <Link to={ctaBtn2Link} className="vg-btn vg-btn--outline">{ctaBtn2Text}</Link>
            )}
          </div>
        </div>
      )
    ),
  };

  const isEmbedded = window.self !== window.top;

  // Wrap each section with highlight outline + click-to-select when in preview
  const wrapSection = (key, el) => {
    if (!el || !isEmbedded) return el;
    const isHighlighted = highlightedSection === key;
    return (
      <div
        key={key}
        onClick={() => window.parent.postMessage({ source: 'naturiq-preview-frame', type: 'sectionClick', payload: key }, '*')}
        style={{
          position: 'relative',
          outline: isHighlighted ? '3px solid #2d6a4f' : '2px solid transparent',
          outlineOffset: '-2px',
          transition: 'outline-color 0.2s',
          cursor: 'pointer',
        }}
        title={`Click to edit: ${key}`}
      >
        {isHighlighted && (
          <div style={{
            position: 'absolute', top: 4, left: 4, zIndex: 9999,
            background: '#2d6a4f', color: '#fff',
            fontSize: 11, fontWeight: 700, padding: '2px 8px',
            borderRadius: 4, pointerEvents: 'none', letterSpacing: '0.04em',
          }}>
            {key}
          </div>
        )}
        {el}
      </div>
    );
  };

  return (
    <main className="vg-page">
      {orderedKeys.map(key => wrapSection(key, sectionComponents[key] || null))}
    </main>
  );
}